import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property, query, queryAssignedElements } from 'lit/decorators.js';
import { style } from './zui-well-css.js';
import '@zywave/zui-icons';
import { cache } from 'lit/directives/cache.js';
import type { PropertyValues } from 'lit';

/**
 * Use `<zui-well>` to attract users' attention to a message. There are various types of wells depending on messaging needs. `<zui-well>` can optionally be delayed in appearing and then animate in. Consumers can specify whether they want it `animated` or it will default to static.
 *
 * @element zui-well
 *
 * @slot - Default, unnamed slot; for inserting content into `<zui-well>`
 * @slot action - Slot in `action` button or link that can also dismiss the well i.e. `<button` `slot="action">Got` `it</button>`
 *
 * @cssprop [--zui-well-margin=1.25rem 0 (20px 0)] - Determine the margin size of the well
 * @cssprop [--zui-well-animation-margin=1.25rem 0 (20px 0)] - Determine the margin size that animates in for animated and dismissible wells
 *
 * @attr {string} well-dismiss - Add this attribute to an element slotted into the `action` slot, when clicked it will close the well if attribute is present, i.e.`<zui-button` `slot="action"` `well-dismiss>`,
 *
 * @event {CustomEvent} dismiss - Event fires when well is dismissed
 */
export class ZuiWell extends ZuiBaseElement {
  /**
   * The type of well to display
   * @type { 'info' | 'warning' | 'error' | 'success' }
   */
  @property({ type: String, reflect: true, attribute: 'type' })
  type: ZuiWellType = 'info';

  /**
   * Whether or not you want to display an icon in your well
   */
  @property({ type: Boolean, attribute: 'no-icon', reflect: true })
  noIcon = false;

  /**
   * When set, a user is presented with the ability to dismiss the well
   */
  @property({ type: Boolean, attribute: 'dismissible', reflect: true })
  dismissible = false;

  /**
   * If set, well spans full width of container
   */
  @property({ type: Boolean, attribute: 'banner', reflect: true })
  banner = false;

  /**
   * If set, the well will slide in on load
   */
  @property({ type: Boolean, attribute: 'animated', reflect: true })
  animated = false;

  @query('.well')
  private _well: HTMLElement;

  @queryAssignedElements({ slot: 'action', flatten: true, selector: '[well-dismiss]' })
  private _dismissActionElements: NodeListOf<HTMLElement>;

  static get styles() {
    return [super.styles, style];
  }

  firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);

    if (this.dismissible) {
      this._dismissActionElements.forEach((actionEl) => {
        actionEl.addEventListener('click', () => {
          this.dismiss();
        });
        actionEl.addEventListener('keyup', (e: KeyboardEvent) => {
          if (e.code === 'Space') {
            this.dismiss();
          }
        });
      });
    }
  }

  render() {
    return html`
      <div class="well" @webkitAnimationEnd="${this._onAnimationEnd}" @animationend="${this._onAnimationEnd}">
        <div class="content">
          <div class="icon">
            <zui-icon icon="${this._computeIcon(this.type)}"></zui-icon>
          </div>
          <div class="text">
            <slot></slot>
            <div class="action-wrap">
              <slot name="action"></slot>
            </div>
          </div>
          ${cache(
            this.dismissible
              ? html`
                  <zui-icon class="small dismiss" icon="zui-remove" @click="${this.dismiss}" tabindex="0"></zui-icon>
                `
              : html``
          )}
        </div>
      </div>
    `;
  }

  /**
   * Use to `dismiss` the well, only runs if `dismissible` is `true`
   */
  public dismiss(): void {
    if (this.dismissible) {
      this._well.style.height = this._well.offsetHeight + 'px';
      // animating based off of changes in height is difficult; cannot animate from auto to 0
      setTimeout(() => {
        this._handleDismiss();
      }, 50); // 50 because the browser will optimize out CSS animations if the property changes quickly
    }
  }

  private _handleDismiss() {
    this._well.classList.add('dismissed');
    this._well.style.height = '0';
    this._well.style.border = '0';
    this.dispatchEvent(new CustomEvent('dismiss', { bubbles: true }));
  }

  /**
   *
   * @param {string} type Computes the _icon property. This determines which zui-icon this well will use.
   */
  private _computeIcon(type: string): string {
    type = type || '';
    const prefix = 'zui';
    let icon = undefined;
    switch (type.toLowerCase()) {
      case 'info':
        icon = 'info';
        break;
      case 'success':
        icon = 'success';
        break;
      case 'warning':
        icon = 'warning';
        break;
      case 'error':
        icon = 'error';
        break;
      default:
        icon = 'info';
        break;
    }

    return `${prefix}-${icon}`;
  }

  private _onAnimationEnd() {
    if (!this._well.classList.contains('dismissed')) {
      this._well.style.height = 'auto';
    }
  }
}

window.customElements.define('zui-well', ZuiWell);

export type ZuiWellType = 'info' | 'warning' | 'error' | 'success';

declare global {
  interface HTMLElementTagNameMap {
    'zui-well': ZuiWell;
  }
}
